// Glue between the low-level gamepadPoll and the high-level
// spatialFocus. The bridge is active only while controllerMode is
// 'menu'. When the game board is up or a text input has focus, the
// bridge stands down so those subsystems keep ownership of input.
//
// Mappings (Xbox layout):
//
//   D-pad up/down/left/right       → spatialFocus.moveFocus(direction)
//   Left stick (past threshold)    → spatialFocus.moveFocus(direction)
//                                    with the same 120 ms debounce as d-pad
//   A                              → spatialFocus.activateFocus()
//   B                              → spatialFocus.back()
//   Start                          → top-scope onBack (treated as "menu" shortcut)
//   RB / LB                        → reserved for page-next / page-prev
//                                    hints where a screen opts in; the
//                                    bridge surfaces these as events the
//                                    screen can subscribe to.

import { GAMEPAD_BUTTON } from '../game/keybindings';
import { createGamepadPoll } from './gamepadPoll';
import { spatialFocus } from './spatialFocus';
import { controllerMode } from './controllerMode';
import { inputSource } from './inputSource';

const STICK_THRESHOLD = 0.55;

function directionFromStick(x, y) {
  if (Math.abs(x) < STICK_THRESHOLD && Math.abs(y) < STICK_THRESHOLD) return null;
  if (Math.abs(x) > Math.abs(y)) return x > 0 ? 'right' : 'left';
  return y > 0 ? 'down' : 'up';
}

export function createGamepadFocusBridge({
  poll = createGamepadPoll({ padIndex: 0 }),
  focus = spatialFocus,
  mode = controllerMode,
  source = inputSource,
} = {}) {
  let running = false;
  let offs = [];
  let stickHoldDirection = null;
  let stickHoldSince = 0;
  let stickLastFireAt = 0;
  const STICK_INITIAL_MS = 300;
  const STICK_REPEAT_MS = 120;

  function isActive() { return mode.get() === 'menu'; }

  function handleButtonDown({ index }) {
    source.report('gamepad');
    if (!isActive()) return;
    switch (index) {
      case GAMEPAD_BUTTON.DPAD_UP:    focus.moveFocus('up'); break;
      case GAMEPAD_BUTTON.DPAD_DOWN:  focus.moveFocus('down'); break;
      case GAMEPAD_BUTTON.DPAD_LEFT:  focus.moveFocus('left'); break;
      case GAMEPAD_BUTTON.DPAD_RIGHT: focus.moveFocus('right'); break;
      case GAMEPAD_BUTTON.A: focus.activateFocus(); break;
      case GAMEPAD_BUTTON.B: focus.back(); break;
      case GAMEPAD_BUTTON.START: focus.back(); break;
      default: break;
    }
  }

  function handleAxis({ axes }) {
    if (!isActive()) {
      stickHoldDirection = null;
      return;
    }
    const dir = directionFromStick(axes[0], axes[1]);
    const t = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (dir === null) {
      stickHoldDirection = null;
      return;
    }
    source.report('gamepad');
    if (dir !== stickHoldDirection) {
      stickHoldDirection = dir;
      stickHoldSince = t;
      stickLastFireAt = t;
      focus.moveFocus(dir);
      return;
    }
    const held = t - stickHoldSince;
    if (held < STICK_INITIAL_MS) return;
    if (t - stickLastFireAt >= STICK_REPEAT_MS) {
      focus.moveFocus(dir);
      stickLastFireAt = t;
    }
  }

  function start() {
    if (running) return;
    running = true;
    offs.push(poll.on('button-down', (e) => handleButtonDown(e.detail)));
    offs.push(poll.on('axis', (e) => handleAxis(e.detail)));
    poll.start();
  }

  function stop() {
    running = false;
    for (const off of offs) off();
    offs = [];
    poll.stop();
  }

  return { start, stop };
}
